import { useQueries, useQuery } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';
import type { NoteLike } from './calc';

export type SalesReturn = Schemas['SalesReturn'];
export type SalesReturnRow = Schemas['SalesReturnList']['data'][number];
export type PurchaseReturn = Schemas['PurchaseReturn'];
export type PurchaseReturnRow = Schemas['PurchaseReturnList']['data'][number];
export type CreditNote = Schemas['CreditNote'];
export type DebitNote = Schemas['DebitNote'];
export type CreditNoteRow = Schemas['CreditNoteList']['data'][number];
export type DebitNoteRow = Schemas['DebitNoteList']['data'][number];
export type SalesInvoice = Schemas['SalesInvoice'];
export type SupplierInvoice = Schemas['SupplierInvoice'];
export type GoodsReceipt = Schemas['GoodsReceipt'];

export type NoteKind = 'credit' | 'debit';

export const keys = {
  salesReturns: ['sales-returns'] as const,
  salesReturn: (id: string) => ['sales-returns', 'detail', id] as const,
  purchaseReturns: ['purchase-returns'] as const,
  purchaseReturn: (id: string) => ['purchase-returns', 'detail', id] as const,
  notes: (kind: NoteKind) => [kind === 'credit' ? 'credit-notes' : 'debit-notes'] as const,
  note: (kind: NoteKind, id: string) =>
    [kind === 'credit' ? 'credit-notes' : 'debit-notes', 'detail', id] as const,
  salesInvoice: (id: string) => ['sales-invoices', 'detail', id] as const,
  supplierInvoice: (id: string) => ['supplier-invoices', 'detail', id] as const,
  goodsReceipt: (id: string) => ['goods-receipts', 'detail', id] as const,
};

/** A credit or debit note in one shape, so the note screens can serve both. */
export interface Note {
  kind: NoteKind;
  id: string;
  number: string;
  contact: { id: string; name: string; phone: string | null };
  status: 'POSTED' | 'CANCELLED';
  docDate: string;
  reason: string | null;
  netTotal: string;
  taxTotal: string;
  total: string;
  allocated: string;
  unallocated: string;
  version: number;
  postedAt: string;
  /** The invoice (credit note) or bill (debit note) it credits. */
  source: { id: string; number: string; extra: string | null };
  /** The return that issued it, when it came from one. */
  returnId: string | null;
  notes: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  allocations: CreditNote['allocations'];
  lines: {
    id: string;
    lineNo: number;
    description: string;
    qtyBase: string | null;
    netAmount: string;
    taxAmount: string;
    lineTotal: string;
    sourceLineId: string;
  }[];
}

export function fromCreditNote(n: CreditNote): Note {
  return {
    ...n,
    kind: 'credit',
    source: { id: n.salesInvoice.id, number: n.salesInvoice.number, extra: null },
    returnId: n.salesReturnId,
    lines: n.lines.map((l) => ({ ...l, sourceLineId: l.invoiceLineId })),
  };
}

export function fromDebitNote(n: DebitNote): Note {
  return {
    ...n,
    kind: 'debit',
    source: {
      id: n.supplierInvoice.id,
      number: n.supplierInvoice.number,
      extra: n.supplierInvoice.supplierInvoiceNumber,
    },
    returnId: n.purchaseReturnId,
    lines: n.lines.map((l) => ({ ...l, sourceLineId: l.supplierInvoiceLineId })),
  };
}

export function fetchNote(kind: NoteKind, id: string): Promise<Note> {
  return kind === 'credit'
    ? unwrap(api.GET('/credit-notes/{id}', { params: { path: { id } } })).then(fromCreditNote)
    : unwrap(api.GET('/debit-notes/{id}', { params: { path: { id } } })).then(fromDebitNote);
}

export function useNote(kind: NoteKind, id: string) {
  return useQuery({ queryKey: keys.note(kind, id), queryFn: () => fetchNote(kind, id) });
}

export function useSalesInvoice(id: string | null) {
  return useQuery({
    queryKey: keys.salesInvoice(id ?? ''),
    queryFn: () => unwrap(api.GET('/sales-invoices/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
  });
}

export function useSupplierInvoice(id: string | null) {
  return useQuery({
    queryKey: keys.supplierInvoice(id ?? ''),
    queryFn: () =>
      unwrap(api.GET('/supplier-invoices/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
  });
}

export function useGoodsReceipt(id: string | null) {
  return useQuery({
    queryKey: keys.goodsReceipt(id ?? ''),
    queryFn: () => unwrap(api.GET('/goods-receipts/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
  });
}

/**
 * Every note already issued against an invoice (credit) or bill (debit), with lines — what the line
 * usage (returned / credited so far) is derived from.
 */
export function useNotesFor(kind: NoteKind, sourceId: string | null) {
  const list = useQuery({
    queryKey: [...keys.notes(kind), 'for', sourceId],
    queryFn: (): Promise<{ data: { id: string; status: string }[] }> =>
      kind === 'credit'
        ? unwrap(
            api.GET('/credit-notes', {
              params: { query: { salesInvoiceId: sourceId ?? '', pageSize: 100 } },
            }),
          )
        : unwrap(
            api.GET('/debit-notes', {
              params: { query: { supplierInvoiceId: sourceId ?? '', pageSize: 100 } },
            }),
          ),
    enabled: Boolean(sourceId),
  });
  const ids = (list.data?.data ?? []).filter((n) => n.status === 'POSTED').map((n) => n.id);
  const details = useQueries({
    queries: ids.map((id) => ({
      queryKey: keys.note(kind, id),
      queryFn: () => fetchNote(kind, id),
    })),
  });
  const loading = list.isLoading || details.some((d) => d.isLoading);
  const error = list.error ?? details.find((d) => d.error)?.error ?? null;
  const notes: NoteLike[] = details.flatMap((d) =>
    d.data
      ? [
          {
            status: d.data.status,
            lines: d.data.lines.map((l) => ({
              lineId: l.sourceLineId,
              qtyBase: l.qtyBase,
              lineTotal: l.lineTotal,
            })),
          },
        ]
      : [],
  );
  return { notes, loading, error, count: ids.length };
}
